import React, { useContext, useState, useRef, useEffect } from 'react';
import Truncate from 'react-truncate';

import { useHistory, useParams, useLocation } from 'react-router-dom';

import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';

import List from '@material-ui/core/List';
import Collapse from '@material-ui/core/Collapse';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import SettingsIcon from '@material-ui/icons/Settings';
import PaymentIcon from '@material-ui/icons/Payment';
import GroupIcon from '@material-ui/icons/Group';
import CheckIcon from '@material-ui/icons/Check';

import AppContext from 'AppContext';
import { getRole, checkPermission } from 'roles/checkPermission';

import { ReactComponent as DropdownIcon } from 'images/icons/dropdown-white.svg';

export default function OrgMenu() {
  const history = useHistory();
  const { teamId } = useParams();
  const location = useLocation();

  const { org, orgs, isAuthenticated, switchOrg, userMeta } = useContext(AppContext);
  const { name, id: orgId } = org || {};
  const [open, setOpen] = useState(false);
  const [orgsOpen, setOrgsOpen] = useState(false);

  const panelEl = useRef(null);

  // Get user role and permissions
  const { role } = getRole({ orgId, userMeta });
  const canEditOrg = checkPermission(role, 'org:edit');
  const canEditBilling = checkPermission(role, 'billing:edit');

  // retro board, create retro page, pulse, invite page don't render org menu
  const noOrgMenu =
    location.pathname.includes('/board') ||
    location.pathname.includes('/invite') ||
    location.pathname.includes('/pulse-survey');

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    orgsOpen && setOrgsOpen(false);
  };

  const handleOrgsOpen = () => {
    setOrgsOpen(!orgsOpen);
  };

  const onOrgSwitch = (id) => {
    if (org?.id !== id) {
      switchOrg({ orgId: id });

      if (location.pathname.includes('/dashboard') || location.pathname.includes('/icebreaker-questions')) {
        history.push('/dashboard');
      }

      setOrgsOpen(false);
    }
  };

  const handleSettingsClick = () => {
    history.push('/settings');
    setOpen(false);
  };

  const handlePlansClick = () => {
    history.push('/plans');
    setOpen(false);
  };

  const renderOrgMenu = () => {
    const renderSwitchMenu = () => {
      return (
        <section className="menu-section">
          <span
            className="text-button"
            onClick={handleOrgsOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 24px',
              cursor: 'pointer',
              margin: '0'
            }}
          >
            <GroupIcon style={{ fontSize: '18px', marginRight: '8px' }} />
            Switch Organization{orgsOpen ? <ExpandLess /> : <ExpandMore />}
          </span>

          <Collapse in={orgsOpen}>
            <List
              className="menuGroups"
              component="div"
              disablePadding
              style={{
                margin: '0 0 8px 0',
                paddingLeft: '41px',
                alignItems: 'flex-start',
                justifyContent: 'flex-start'
              }}
            >
              {orgs.map(({ id, name }) => {
                const isSelected = Boolean(id === orgId);
                return (
                  <button
                    key={`orgMenu-${id}`}
                    onClick={() => onOrgSwitch(id)}
                    className={`${isSelected ? 'menuGroups-group-selected' : ''}`}
                    title={name} // Show full name on hover
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px'
                    }}
                  >
                    <Truncate width={isSelected ? 220 : 250}>{name}</Truncate>
                    {isSelected && <CheckIcon style={{ fontSize: '16px', color: '#fff' }} />}
                  </button>
                );
              })}
            </List>
          </Collapse>
        </section>
      );
    };

    const renderOwnerActions = () => {
      if (!canEditOrg && !canEditBilling) return null;

      return (
        <section className="menu-section">
          {canEditOrg && (
            <div className="menu-item" onClick={handleSettingsClick}>
              <SettingsIcon style={{ fontSize: '18px', marginRight: '8px' }} />
              <span>Organization Settings</span>
            </div>
          )}
          {canEditBilling && (
            <div className="menu-item" onClick={handlePlansClick}>
              <PaymentIcon style={{ fontSize: '18px', marginRight: '8px' }} />
              <span>Plans & Billing</span>
            </div>
          )}
        </section>
      );
    };

    return (
      <>
        <span
          style={{
            display: 'flex',
            margin: '12px',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {name}
        </span>

        {orgs.length > 1 && renderSwitchMenu()}
        {renderOwnerActions()}
      </>
    );
  };

  useEffect(() => {
    if (noOrgMenu) return;

    if (org && orgs.length > 0 && teamId) {
      if (org.teams.includes(teamId)) return;

      const validOrg = orgs.filter(({ teams }) => teams.includes(teamId))[0];

      if (validOrg) {
        const { id } = validOrg;
        switchOrg({ orgId: id });
      } else {
        //Handle invalid teamId
        history.push('/dashboard');
      }
    }
  }, [org, orgs, teamId, switchOrg, history, noOrgMenu]);

  if (!isAuthenticated || noOrgMenu) return null;

  return (
    <>
      <div className="orgMenu" onMouseOver={handleOpen} onMouseLeave={handleClose}>
        <h1 ref={panelEl} className="boardTitle">
          {name}
          <span>
            <DropdownIcon />
          </span>
        </h1>

        <div className="actionPanelList">
          {/** Important: this popper has no disablePortal, as it causing overflow in safari **/}
          <Popper style={{ zIndex: 1 }} anchorEl={panelEl.current} open={open} placement="bottom" transition>
            {({ TransitionProps, placement }) => (
              <Grow {...TransitionProps} style={{ transformOrigin: 'center top' }}>
                <Paper>
                  <div className="actionPanelBoard actionPanelBoard-orgMenu">
                    <h3>Organization</h3>
                    {renderOrgMenu()}
                  </div>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      </div>
    </>
  );
}
